import { Button, Drawer, Group, Stack, Text } from '@mantine/core';
import { useDisclosure, useMediaQuery } from '@mantine/hooks';
import { useTranslation } from 'react-i18next';

import { TravelActivityType, TravelDayBalance, TravelDayBalanceYear } from '../model/TravelRequestTypes';
import { TravelDayBalanceTrips, travelFormationActivityTypes } from './TravelDayBalanceCommon';
import { TravelDayBalanceSection, TravelDayBalanceSectionYear } from './TravelDayBalanceSection';

interface TravelRequestDayBalanceProps {
  activityType: TravelActivityType;
  leaveDaysExceeded: boolean;
  dayCapExceeded: boolean;
  dayBalance: TravelDayBalance | null;
}

function professionalDevelopmentPool(year: TravelDayBalanceYear) {
  return {
    used: year.professionalDevelopmentUsed,
    requested: year.thisRequestProfessionalDevelopmentDays,
    cap: year.professionalDevelopmentCap,
  };
}

export function TravelRequestDayBalance({
  activityType,
  leaveDaysExceeded,
  dayCapExceeded,
  dayBalance,
}: TravelRequestDayBalanceProps) {
  const { t } = useTranslation();
  const [opened, { open, close }] = useDisclosure(false);
  const narrow = useMediaQuery('(max-width: 48em)');
  const formation = travelFormationActivityTypes.includes(activityType);
  const multiYear = (dayBalance?.years.length ?? 0) > 1;

  const years: TravelDayBalanceSectionYear[] =
    dayBalance?.years.map(year => ({
      year: year.year,
      withPaperUsed: year.withPaperUsed,
      thisRequestPaidDays: year.thisRequestPaidDays,
      withPaperCap: year.withPaperCap,
      exceeded: year.withPaperAfter > year.withPaperCap,
      vacationDays: year.vacationDays,
      unpaidDays: year.unpaidDays,
      thisRequestVacationDays: year.thisRequestVacationDays,
      thisRequestUnpaidDays: year.thisRequestUnpaidDays,
      professionalDevelopment: formation ? professionalDevelopmentPool(year) : undefined,
    })) ?? [];
  const tripYears = dayBalance?.consumes === true ? dayBalance.years.filter(({ trips }) => trips.length > 0) : [];
  const tripCount = tripYears.reduce((count, { trips }) => count + trips.length, 0);

  return (
    <TravelDayBalanceSection
      activityType={activityType}
      leaveDaysExceeded={leaveDaysExceeded}
      dayCapExceeded={dayCapExceeded}
      dayBalance={dayBalance == null ? null : { consumes: dayBalance.consumes, years }}
    >
      {tripCount > 0 && (
        <>
          <Group>
            <Button
              size="compact-xs"
              variant="default"
              onClick={open}
            >
              {t('travel.requests.dayBalance.detail.tripsButton', { count: tripCount })}
            </Button>
          </Group>
          <Drawer
            opened={opened}
            onClose={close}
            position="right"
            size={narrow ? '100%' : 800}
            title={t('travel.requests.dayBalance.detail.trips')}
            closeButtonProps={{ 'aria-label': t('button.close') }}
          >
            <Stack gap="md">
              {tripYears.map(({ year, trips }) => (
                <Stack
                  key={year}
                  gap={4}
                >
                  {multiYear && (
                    <Text
                      size="sm"
                      fw={500}
                    >
                      {year}
                    </Text>
                  )}
                  <TravelDayBalanceTrips trips={trips} />
                </Stack>
              ))}
            </Stack>
          </Drawer>
        </>
      )}
    </TravelDayBalanceSection>
  );
}
